import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import { useAuth } from "../context/AuthContext";
import "./ReportsSectionCompact.css";
import {
  getSalonValuationConfig,
  runSalonValuationReport,
  updateSalonValuationConfig,
} from "../api/salonValuationReport";

dayjs.extend(utc);
dayjs.extend(timezone);

const HELP_SECTIONS = [
  {
    id: "snapshot",
    title: "Valuation Snapshot",
    description: "Estimated range, midpoint, and confidence at a glance.",
    anchorId: "sv-snapshot",
  },
  {
    id: "drivers",
    title: "Key Drivers",
    description: "Measured revenue/SDE and the multipliers used in the estimate.",
    anchorId: "sv-drivers",
  },
  {
    id: "kpi-evidence",
    title: "KPI Evidence",
    description: "Operational evidence that supports or weakens valuation confidence.",
    anchorId: "sv-kpis",
  },
  {
    id: "assumptions",
    title: "Assumptions & Inputs",
    description: "Editable assumptions separated from measured metrics.",
    anchorId: "sv-assumptions",
  },
  {
    id: "improve-value",
    title: "Improve Your Value",
    description: "Action-focused recommendations based on current performance.",
    anchorId: "sv-recommendations",
  },
];

const INPUT_CLASS =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-200";

const EDITABLE_VALUATION_FIELDS = [
  "sde_multiple_low",
  "sde_multiple_high",
  "revenue_multiple_low",
  "revenue_multiple_high",
  "lease_risk_adjustment_pct",
  "owner_dependence_adjustment_pct",
  "equipment_value",
  "inventory_value",
  "leasehold_improvements_value",
  "rebook_window_days",
];

const formatMoney = (value) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "$0.00";
  const abs = Math.abs(num).toFixed(2);
  return num < 0 ? `-$${abs}` : `$${abs}`;
};

const formatPct = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "Not enough data yet";
  return `${num.toFixed(2)}%`;
};

const formatNumber = (value, digits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "Not enough data yet";
  return num.toFixed(digits);
};

const formatLabelList = (items) => {
  const values = (items || []).filter(Boolean);
  if (values.length <= 1) return values[0] || "";
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
};

const confidenceBadgeClass = (label) => {
  if (label === "High") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (label === "Medium") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-rose-100 text-rose-700 border-rose-200";
};

export default function SalonValuationReport() {
  const navigate = useNavigate();
  const { token, currentSalon, user, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [config, setConfig] = useState(null);
  const [report, setReport] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  const salonId = useMemo(() => currentSalon?.id ?? user?.salon_id ?? null, [currentSalon, user]);
  const salonTz = useMemo(
    () =>
      currentSalon?.timezone ||
      currentSalon?.business?.timezone ||
      user?.salon_timezone ||
      authSalonTimezone ||
      "UTC",
    [currentSalon, user, authSalonTimezone]
  );

  useEffect(() => {
    if (!salonTz) return;
    const now = dayjs().tz(salonTz);
    const defaultEnd = now.format("YYYY-MM-DD");
    const defaultStart = now.subtract(12, "month").add(1, "day").format("YYYY-MM-DD");
    setEndDate((prev) => prev || defaultEnd);
    setStartDate((prev) => prev || defaultStart);
  }, [salonTz]);

  const loadConfig = useCallback(async () => {
    if (!token || !salonId) return;
    setLoadingConfig(true);
    setError("");
    try {
      const data = await getSalonValuationConfig(token, salonId);
      setConfig(data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load valuation settings.");
    } finally {
      setLoadingConfig(false);
    }
  }, [token, salonId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const runReport = useCallback(async () => {
    if (!token || !salonId || !startDate || !endDate) return;
    if (startDate > endDate) {
      setError("End date cannot be before start date.");
      return;
    }
    setRunning(true);
    setError("");
    try {
      const data = await runSalonValuationReport(
        { start_date: startDate, end_date: endDate, salon_id: salonId },
        token
      );
      setReport(data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to run estimated value report.");
    } finally {
      setRunning(false);
    }
  }, [token, salonId, startDate, endDate]);

  useEffect(() => {
    if (config && startDate && endDate) {
      runReport();
    }
  }, [config, startDate, endDate, runReport]);

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...(prev || {}), [field]: value }));
    setSaveMessage("");
  };

  const handleSave = async () => {
    if (!token || !salonId || !config) return;
    setSaving(true);
    setError("");
    setSaveMessage("");
    try {
      const payload = { ...config };
      delete payload.payroll_mode;
      delete payload.payroll_percent;
      delete payload.rent_monthly;
      delete payload.supplies_percent;
      delete payload.merchant_fees_percent;
      delete payload.other_overhead_monthly;
      delete payload.owner_addbacks_annual;
      delete payload.one_time_expenses_annual;
      EDITABLE_VALUATION_FIELDS.forEach((field) => {
        const parsed = Number(payload[field]);
        payload[field] = Number.isFinite(parsed) ? parsed : 0;
      });
      payload.rebook_window_days = Math.max(1, Math.floor(Number(payload.rebook_window_days || 30)));
      const saved = await updateSalonValuationConfig(payload, token, salonId);
      setConfig(saved || payload);
      setSaveMessage("Assumptions saved.");
      await runReport();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save assumptions.");
    } finally {
      setSaving(false);
    }
  };

  const valuation = report?.valuation_snapshot || {};
  const kpis = report?.kpis || {};
  const keyDrivers = report?.key_drivers || {};
  const sanity = report?.sanity_checks || {};
  const notes = report?.kpi_notes || {};
  const calculations = report?.calculation_details || {};
  const kpiCalculations = report?.kpi_calculations || {};
  const notices = Array.isArray(report?.notices) ? report.notices : [];
  const profitabilityExpenseMeta = calculations?.expenses || {};
  const profitabilityExpenseLines = Array.isArray(profitabilityExpenseMeta?.lines)
    ? profitabilityExpenseMeta.lines
    : [];
  const needsProfitabilityInputs = Boolean(
    profitabilityExpenseMeta?.needs_profitability_inputs
  );
  const profitabilityMissingFields = Array.isArray(
    profitabilityExpenseMeta?.missing_fields
  )
    ? profitabilityExpenseMeta.missing_fields
    : [];
  const profitabilityRoute =
    profitabilityExpenseMeta?.profitability_report_route ||
    "/manager/reports/profitability-estimate";

  return (
    <div className="reportSectionShell">
      <div className="reportSectionWide report-section-tight report-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Estimate Business Value" />

          <div className="px-6 py-6 space-y-6">
            <ReportHelpCard
              title="Help Guide: Estimate Business Value"
              subtitle="Review valuation range, confidence, and assumption inputs."
              sections={HELP_SECTIONS}
              onJumpToSection={jumpToSectionWithHighlight}
            />

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Start date</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={INPUT_CLASS} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">End date</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={INPUT_CLASS} />
                </div>
                <button type="button" onClick={runReport} disabled={running || loadingConfig} className="h-10 rounded-full bg-slate-900 px-5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
                  {running ? "Running..." : "Run Report"}
                </button>
              </div>
              <div className="text-xs text-slate-500">Timezone: {report?.date_range?.tz_name || salonTz}</div>
            </div>

            {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">{error}</div>}
            {saveMessage && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">{saveMessage}</div>}
            {notices.map((item) => (
              <div key={item} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">{item}</div>
            ))}

            <div id="sv-snapshot" className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${getSectionClassName("sv-snapshot")}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">Valuation Snapshot</h2>
                <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${confidenceBadgeClass(valuation.confidence_label)}`}>
                  {valuation.confidence_label || "Low"} confidence
                </span>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">Estimated Value Range <InfoTooltip text="Primary output based on SDE multiple range with adjustments." label="Estimated Value Range" /></div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">{formatMoney(valuation.estimated_low)} - {formatMoney(valuation.estimated_high)}</div>
                  {calculations?.valuation && (
                    <div className="mt-2 text-xs text-slate-500">
                      {formatMoney(calculations.valuation.adjusted_low)} to{" "}
                      {formatMoney(calculations.valuation.adjusted_high)} after adjustments.
                    </div>
                  )}
                  {calculations?.valuation?.valuation_zero_floor_applied && (
                    <div className="mt-2 text-xs text-amber-700">
                      Negative SDE is floored at $0.00 for the valuation range.
                    </div>
                  )}
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">Best Estimate <InfoTooltip text="Midpoint of the valuation range." label="Best Estimate" /></div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">{formatMoney(valuation.midpoint)}</div>
                  {calculations?.valuation && (
                    <div className="mt-2 text-xs text-slate-500">
                      ({formatMoney(calculations.valuation.adjusted_low)} +{" "}
                      {formatMoney(calculations.valuation.adjusted_high)}) / 2
                    </div>
                  )}
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">Confidence Score <InfoTooltip text="0-100 score from revenue verification, expense completeness, stability, and transparency." label="Confidence Score" /></div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">{Number(valuation.confidence_score || 0)}</div>
                  <div className="mt-2 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-rose-500" style={{ width: `${Math.max(0, Math.min(100, Number(valuation.confidence_score || 0)))}%` }} /></div>
                  <div className="mt-2 text-xs text-slate-500">{valuation.method || "Income Approach (SDE multiple)"}</div>
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-4 space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">How this estimate is calculated</div>
                  <div className="mt-2 text-sm text-slate-700">
                    {calculations?.sde_definition ||
                      "SDE means Seller's Discretionary Earnings. In this report it is calculated as revenue minus operating expenses, with owner and one-time add-backs excluded."}
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="text-xs uppercase tracking-wide text-slate-500">T12 SDE Math</div>
                    <div className="mt-2 text-sm text-slate-700">
                      Revenue - operating expenses
                    </div>
                    <div className="mt-2 text-sm text-slate-700">
                      {formatMoney(calculations?.sde?.revenue)} -{" "}
                      {formatMoney(calculations?.sde?.operating_expenses)}
                    </div>
                    <div className="mt-2 font-semibold text-slate-900">
                      = {formatMoney(calculations?.sde?.sde)}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Operating Expense Math
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-slate-700">
                      {profitabilityExpenseLines.length === 0 && (
                        <div>Operating expenses are missing from Profitability Estimate.</div>
                      )}
                      {profitabilityExpenseLines.map((line) => (
                        <div key={`${line.item_key || line.label}-${line.total}`}>
                          {line.label}: <span className="font-semibold text-slate-900">{formatMoney(line.total)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 font-semibold text-slate-900">
                      Total operating expenses = {formatMoney(calculations?.sde?.operating_expenses)}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      Source: Profitability Estimate
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="text-xs uppercase tracking-wide text-slate-500">Value Range Math</div>
                    {calculations?.valuation?.valuation_zero_floor_applied && (
                      <div className="mt-2 text-sm text-amber-700">
                        SDE is negative, so the report uses max(SDE, $0.00) before applying the valuation multiple.
                      </div>
                    )}
                    <div className="mt-2 text-sm text-slate-700">
                      Low: {formatMoney(calculations?.valuation?.valuation_sde ?? calculations?.valuation?.sde)} x{" "}
                      {formatNumber(calculations?.valuation?.sde_multiple_low)} ={" "}
                      {formatMoney(calculations?.valuation?.low_value_before_adjustments)}
                    </div>
                    <div className="mt-1 text-sm text-slate-700">
                      High: {formatMoney(calculations?.valuation?.valuation_sde ?? calculations?.valuation?.sde)} x{" "}
                      {formatNumber(calculations?.valuation?.sde_multiple_high)} ={" "}
                      {formatMoney(calculations?.valuation?.high_value_before_adjustments)}
                    </div>
                    <div className="mt-2 text-sm text-slate-700">
                      Adjustment factor: lease {formatPct(calculations?.valuation?.lease_risk_adjustment_pct)} and owner dependence{" "}
                      {formatPct(calculations?.valuation?.owner_dependence_adjustment_pct)}
                    </div>
                    <div className="mt-1 text-sm text-slate-700">
                      Net adjustment impact: {formatPct(calculations?.valuation?.adjustment_factor_percent)}
                    </div>
                    <div className="mt-2 font-semibold text-slate-900">
                      Final range: {formatMoney(calculations?.valuation?.adjusted_low)} -{" "}
                      {formatMoney(calculations?.valuation?.adjusted_high)}
                    </div>
                  </div>
                </div>
              </div>
              {needsProfitabilityInputs && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
                  <div className="font-semibold">Complete the missing expense inputs in Profitability Estimate.</div>
                  <div className="mt-1 text-amber-800">
                    {profitabilityMissingFields.length > 0
                      ? `Add ${formatLabelList(profitabilityMissingFields)} in Profitability Estimate, then rerun valuation.`
                      : "Add the missing operating expense lines in Profitability Estimate, then rerun valuation."}
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => navigate(profitabilityRoute)}
                      className="rounded-full border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                    >
                      Open Profitability Estimate
                    </button>
                  </div>
                </div>
              )}
              {(report?.confidence_breakdown || []).length > 0 && (
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Confidence Breakdown</div>
                  <ul className="space-y-1 text-sm text-slate-700">
                    {(report?.confidence_breakdown || []).map((item) => (
                      <li key={item.component} className="flex items-center justify-between gap-3">
                        <span>{item.component}</span>
                        <span className="font-semibold text-slate-900">{item.points}/{item.max_points}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div id="sv-drivers" className={`rounded-2xl border border-slate-200 bg-white p-5 ${getSectionClassName("sv-drivers")}`}>
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">Key Drivers</h2>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="text-xs uppercase text-slate-500">T12 Revenue</div>
                  <div className="mt-1 font-semibold text-slate-900">{formatMoney(keyDrivers.t12_revenue)}</div>
                  <div className="mt-2 text-xs text-slate-500">Net sales after refunds in the selected range.</div>
                </div>
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center gap-2 text-xs uppercase text-slate-500">
                    T12 SDE
                    <InfoTooltip
                      text="SDE means Seller's Discretionary Earnings. In this report it is calculated as revenue minus operating expenses, with owner and one-time add-backs excluded."
                      label="T12 SDE"
                    />
                  </div>
                  <div className="mt-1 font-semibold text-slate-900">{formatMoney(keyDrivers.t12_sde)}</div>
                  <div className="mt-2 text-xs text-slate-500">
                    {formatMoney(calculations?.sde?.revenue)} - {formatMoney(calculations?.sde?.operating_expenses)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="text-xs uppercase text-slate-500">SDE Multiple</div>
                  <div className="mt-1 font-semibold text-slate-900">{Number(keyDrivers.sde_multiple_low || 0).toFixed(2)}x - {Number(keyDrivers.sde_multiple_high || 0).toFixed(2)}x</div>
                  <div className="mt-2 text-xs text-slate-500">Buyer multiple applied to SDE.</div>
                </div>
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="text-xs uppercase text-slate-500">Adjustments</div>
                  <div className="mt-1 font-semibold text-slate-900">{formatPct(keyDrivers?.adjustments?.lease_risk_adjustment_pct || 0)} / {formatPct(keyDrivers?.adjustments?.owner_dependence_adjustment_pct || 0)}</div>
                  <div className="mt-2 text-xs text-slate-500">
                    Combined impact: {formatPct(calculations?.valuation?.adjustment_factor_percent || 0)}
                  </div>
                </div>
              </div>
            </div>

            <div id="sv-kpis" className={`rounded-2xl border border-slate-200 bg-white p-5 ${getSectionClassName("sv-kpis")}`}>
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">KPI Evidence</h2>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {[
                  ["Repeat rate", kpis.repeat_rate, "repeat_rate"],
                  ["Rebooking rate", kpis.rebook_rate, "rebook_rate"],
                  ["Avg ticket", kpis.avg_ticket, "avg_ticket"],
                  ["Utilization", kpis.utilization_pct, "utilization_pct"],
                  ["No-show rate", kpis.no_show_rate, "no_show_rate"],
                  ["Cancellation rate", kpis.cancellation_rate, "cancellation_rate"],
                  ["Discount rate", kpis.discount_rate, "discount_rate"],
                ].map(([label, value, key]) => (
                  <div key={key} className="rounded-xl border border-slate-200 p-3">
                    <div className="text-xs uppercase text-slate-500 flex items-center gap-2">
                      {label}
                      {notes[key] && <InfoTooltip text={notes[key]} label={String(label)} />}
                    </div>
                    <div className="mt-1 font-semibold text-slate-900">
                      {key === "avg_ticket"
                        ? Number.isFinite(Number(value))
                          ? formatMoney(value)
                          : "Not enough data yet"
                        : formatPct(value)}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      {key === "repeat_rate" &&
                        `${kpiCalculations?.repeat_rate?.repeat_clients || 0} repeat clients / ${kpiCalculations?.repeat_rate?.total_clients || 0} total clients`}
                      {key === "rebook_rate" &&
                        `${kpiCalculations?.rebook_rate?.rebooked_clients || 0} rebooked clients / ${kpiCalculations?.rebook_rate?.served_clients || 0} served clients in ${kpiCalculations?.rebook_rate?.rebook_window_days || 30} days`}
                      {key === "avg_ticket" &&
                        `${formatMoney(kpiCalculations?.avg_ticket?.revenue)} / ${kpiCalculations?.avg_ticket?.ticket_count || 0} tickets`}
                      {key === "utilization_pct" &&
                        `${formatNumber(kpiCalculations?.utilization_pct?.booked_hours)} booked hrs / ${formatNumber(kpiCalculations?.utilization_pct?.available_hours)} available hrs`}
                      {key === "no_show_rate" &&
                        `${kpiCalculations?.no_show_rate?.no_show_count || 0} no-shows / ${kpiCalculations?.no_show_rate?.total_appointments || 0} appointments`}
                      {key === "cancellation_rate" &&
                        `${kpiCalculations?.cancellation_rate?.cancelled_count || 0} cancellations / ${kpiCalculations?.cancellation_rate?.total_appointments || 0} appointments`}
                      {key === "discount_rate" &&
                        `${formatMoney(kpiCalculations?.discount_rate?.discount_total)} / ${formatMoney(kpiCalculations?.discount_rate?.gross_before_discount)}`}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-xl border border-slate-200 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Service mix (Top 5)</div>
                <ul className="mt-2 space-y-1 text-sm text-slate-700">
                  {(kpis.service_mix_top || []).length === 0 && <li>Not enough data yet</li>}
                  {(kpis.service_mix_top || []).map((row) => (
                    <li key={`${row.service_name}-${row.category_name || ""}`} className="flex items-center justify-between">
                      <span>{row.service_name}{row.category_name ? ` (${row.category_name})` : ""}</span>
                      <span className="font-semibold text-slate-900">{formatMoney(row.net_sales)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div id="sv-assumptions" className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${getSectionClassName("sv-assumptions")}`}>
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">Assumptions & Inputs</h2>
                <button type="button" onClick={handleSave} disabled={saving || loadingConfig || !config} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
              </div>
              {loadingConfig || !config ? (
                <div className="text-slate-500">Loading assumptions...</div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    Operating expenses now come from the Profitability Estimate report, not valuation assumptions.
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => navigate("/manager/reports/profitability-estimate")}
                        className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100"
                      >
                        Open Profitability Estimate
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {EDITABLE_VALUATION_FIELDS.map((field) => (
                    <label key={field} className="text-sm text-slate-700">{field.replaceAll("_", " ")}<input type="number" className={INPUT_CLASS} value={config[field] ?? ""} onChange={(e) => updateField(field, e.target.value)} /></label>
                  ))}
                  <label className="text-sm text-slate-700 md:col-span-2 lg:col-span-3">Notes<textarea className={INPUT_CLASS} rows={3} value={config.notes || ""} onChange={(e) => updateField("notes", e.target.value)} /></label>
                </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-2">Sanity Checks</h2>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="text-xs uppercase text-slate-500">Revenue multiple range</div>
                  <div className="mt-1 font-semibold text-slate-900">{formatMoney(sanity.revenue_multiple_low)} - {formatMoney(sanity.revenue_multiple_high)}</div>
                  <div className="mt-2 text-xs text-slate-500">
                    {formatMoney(calculations?.sanity_checks?.revenue)} x {formatNumber(calculations?.sanity_checks?.revenue_multiple_low)} to {formatNumber(calculations?.sanity_checks?.revenue_multiple_high)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="text-xs uppercase text-slate-500">Asset floor</div>
                  <div className="mt-1 font-semibold text-slate-900">{formatMoney(sanity.asset_floor)}</div>
                  <div className="mt-2 text-xs text-slate-500">
                    {formatMoney(calculations?.sanity_checks?.equipment_value)} + {formatMoney(calculations?.sanity_checks?.inventory_value)} + {formatMoney(calculations?.sanity_checks?.leasehold_improvements_value)}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-500">Used as secondary checks. Primary output is the SDE multiple valuation.</p>
            </div>

            <div id="sv-recommendations" className={`rounded-2xl border border-slate-200 bg-white p-5 ${getSectionClassName("sv-recommendations")}`}>
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-3">Improve Your Value</h2>
              <ul className="space-y-2">
                {(report?.recommendations || []).length === 0 && <li className="text-slate-500">No recommendations yet.</li>}
                {(report?.recommendations || []).map((item, idx) => (
                  <li key={`${item.title}-${idx}`} className="rounded-xl border border-slate-200 p-3">
                    <div className="font-semibold text-slate-900">{item.title}</div>
                    <div className="text-sm text-slate-600 mt-1">{item.impact_hint}</div>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                      {item.metric_before && <span className="rounded-full bg-slate-100 px-2 py-1">Current: {item.metric_before}</span>}
                      {item.target && <span className="rounded-full bg-rose-50 px-2 py-1 text-rose-700">Target: {item.target}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
